import authRouteConfig from "./authRouteConfig.json";

type AuthRedirectOptions = {
  hasRefreshToken: boolean;
  pathname: string;
  referrer?: string | null;
  search?: string;
};

export const REFRESH_TOKEN_COOKIE_NAME = authRouteConfig.refreshTokenCookieName;

const ALLOWED_MISSING_REFRESH_TOKEN_REFERRER_HOSTNAMES = [
  "billing.stripe.com",
  "billing.stripe.network",
  "billing.snobase.com",
  "billing.snobase-dev.net",
  "checkout.stripe.com",
  "checkout.stripe.network",
  "checkout.snobase.com",
  "checkout.snobase-dev.net",
];

function getIsExactOrChildPath(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function getIsPublicPath(pathname: string) {
  return authRouteConfig.publicRoutePrefixes.some((prefix) =>
    getIsExactOrChildPath(pathname, prefix),
  );
}

export function getIsProtectedPath(pathname: string) {
  return !getIsPublicPath(pathname);
}

export function getAuthenticatedRedirectPath(pathname: string) {
  return authRouteConfig.authenticatedRedirects.find(({ source }) =>
    getIsExactOrChildPath(pathname, source),
  )?.destination;
}

export function getAuthenticatedRedirectNextPath(nextPath: unknown) {
  if (typeof nextPath !== "string") return;
  if (!nextPath.startsWith("/") || nextPath.startsWith("//")) return;

  const nextPathPathname = nextPath.split(/[?#]/)[0];
  const isRedirectingToAuthenticatedRoute =
    authRouteConfig.authenticatedRedirects.some(({ source }) =>
      getIsExactOrChildPath(nextPathPathname, source),
    );

  if (isRedirectingToAuthenticatedRoute) return;

  return nextPath;
}

function getUnauthenticatedRedirectPath(pathname: string, search?: string) {
  const params = new URLSearchParams({
    next: `${pathname}${search || ""}`,
  });

  return `/login?${params.toString()}`;
}

function getAuthenticatedRedirectDestination(
  pathname: string,
  search?: string,
) {
  const authenticatedRedirectPath = getAuthenticatedRedirectPath(pathname);
  if (!authenticatedRedirectPath) return;

  const nextPath = new URLSearchParams(search).get("next");

  return (
    getAuthenticatedRedirectNextPath(nextPath) || authenticatedRedirectPath
  );
}

function getReferrerHostname(referrer: string | null = null) {
  if (!referrer) return;

  try {
    return new URL(referrer).hostname;
  } catch (error) {
    if (
      typeof error === "object" &&
      error !== null &&
      "name" in error &&
      error.name === "TypeError"
    )
      return;

    throw error;
  }
}

function getIsAllowedMissingRefreshTokenReferrer(
  referrer: string | null = null,
) {
  const hostname = getReferrerHostname(referrer);
  if (!hostname) return false;

  return ALLOWED_MISSING_REFRESH_TOKEN_REFERRER_HOSTNAMES.includes(hostname);
}

export function getAuthRedirectPath({
  hasRefreshToken,
  pathname,
  referrer,
  search,
}: AuthRedirectOptions) {
  if (hasRefreshToken)
    return getAuthenticatedRedirectDestination(pathname, search);

  if (getIsPublicPath(pathname)) return;
  if (getIsAllowedMissingRefreshTokenReferrer(referrer)) return;

  return getUnauthenticatedRedirectPath(pathname, search);
}
